import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_APP_API_URL;
const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const short = (a) => a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '';

/**
 * ViBirr messaging — the same conversations the user has in the ViBirr app.
 * Works as a slide-over drawer (open/onClose) or an inline panel (inline).
 * Pass `recipient` {address, name} to jump straight into that thread.
 */
export default function BpChat({ open = true, onClose, inline = false, recipient = null }) {
    const [me, setMe] = useState(null);
    const [needWallet, setNeedWallet] = useState(false);
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);      // {id?, other_address, other_name}
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const bodyRef = useRef(null);
    const pollRef = useRef(null);

    const loadInbox = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/vibirr/inbox`, { headers: auth() });
            if (r.data.needWallet) { setNeedWallet(true); setLoading(false); return null; }
            if (r.data.status) {
                setMe(r.data.me);
                const list = (r.data.inbox && r.data.inbox.conversations) || [];
                setConvs(list);
                setLoading(false);
                return list;
            }
        } catch (e) { /* ignore */ }
        setLoading(false);
        return null;
    }, []);

    const loadThread = useCallback(async (convId) => {
        try {
            const r = await axios.get(`${API}/api/vibirr/conversation/${convId}`, { headers: auth() });
            if (r.data.status) {
                setMessages(r.data.messages || (r.data.conversation && r.data.conversation.messages) || []);
            }
        } catch (e) { /* ignore */ }
    }, []);

    // open → inbox, and jump to the recipient's thread if one was given
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        loadInbox().then(list => {
            if (recipient && recipient.address) {
                const existing = (list || []).find(c =>
                    (c.other_address || '').toLowerCase() === recipient.address.toLowerCase());
                setActive(existing
                    ? { id: existing.id, other_address: existing.other_address, other_name: existing.other_name || recipient.name }
                    : { id: null, other_address: recipient.address.toLowerCase(), other_name: recipient.name });
                setMessages([]);
                if (existing) loadThread(existing.id);
            }
        });
    }, [open, recipient, loadInbox, loadThread]);

    // poll the active thread
    useEffect(() => {
        clearInterval(pollRef.current);
        if (open && active && active.id) {
            pollRef.current = setInterval(() => loadThread(active.id), 5000);
        }
        return () => clearInterval(pollRef.current);
    }, [open, active, loadThread]);

    // autoscroll on new messages
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages]);

    const openConv = (c) => {
        setActive({ id: c.id, other_address: c.other_address, other_name: c.other_name });
        setMessages([]);
        loadThread(c.id);
    };

    const send = async (e) => {
        e.preventDefault();
        const t = text.trim();
        if (!t || sending || !active) return;
        setSending(true);
        try {
            const body = active.id
                ? { conversation_id: active.id, message_text: t }
                : { recipient_address: active.other_address, message_text: t };
            const r = await axios.post(`${API}/api/vibirr/message`, body, { headers: auth() });
            if (r.data.status === false) throw new Error(r.data.error || r.data.message || 'Send failed');
            setText('');
            // a first message creates the conversation — pick up its id
            const newId = r.data.conversation_id || (r.data.message && r.data.message.conversation_id) || active.id;
            if (!active.id && newId) {
                setActive(a => ({ ...a, id: newId }));
                loadThread(newId);
            } else if (active.id) {
                loadThread(active.id);
            }
            loadInbox();
        } catch (err) {
            toast.error(err.message || 'Could not send the message');
        } finally {
            setSending(false);
        }
    };

    if (!open) return null;

    const inner = (
        <div className={`bp-chat ${inline ? 'inline' : ''}`}>
            <div className="bp-chat-head">
                {active ? (
                    <>
                        <button className="bp-chat-back" onClick={() => { setActive(null); setMessages([]); loadInbox(); }} aria-label="Back to inbox">←</button>
                        <div className="bp-chat-peer">
                            <strong>{active.other_name || short(active.other_address)}</strong>
                            <span className="bp-mono bp-dim">{short(active.other_address)}</span>
                        </div>
                    </>
                ) : (
                    <div className="bp-chat-peer">
                        <strong>Messages</strong>
                        <span className="bp-dim">synced with <em className="bp-chat-vb">ViBirr</em></span>
                    </div>
                )}
                {!inline && <button className="bp-chat-close" onClick={onClose} aria-label="Close chat">✕</button>}
            </div>

            {needWallet ? (
                <div className="bp-chat-empty">
                    <span aria-hidden="true">🔐</span>
                    <p>Messaging runs on your wallet identity.<br />Sign in with your wallet to chat.</p>
                </div>
            ) : active ? (
                <>
                    <div className="bp-chat-body" ref={bodyRef}>
                        {messages.length === 0 && (
                            <div className="bp-chat-empty small">
                                <p>Say hello — this conversation also appears in ViBirr and BirrWallet.</p>
                            </div>
                        )}
                        {messages.map((m, i) => {
                            const mine = me && (m.sender_address || '').toLowerCase() === me;
                            const when = m.created_at || m.timestamp;
                            return (
                                <div className={`bp-msg ${mine ? 'mine' : ''}`} key={m.id || i}>
                                    <div className="bp-msg-bubble">{m.message_text || m.text}</div>
                                    <span className="bp-msg-time">{when ? moment(when).format('MMM D, HH:mm') : ''}</span>
                                </div>
                            );
                        })}
                    </div>
                    <form className="bp-chat-composer" onSubmit={send}>
                        <input
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder="Write a message…"
                            maxLength={2000}
                        />
                        <button type="submit" className="bp-btn-primary" disabled={sending || !text.trim()}>
                            {sending ? '…' : 'Send'}
                        </button>
                    </form>
                </>
            ) : (
                <div className="bp-chat-body">
                    {loading ? (
                        <div className="bp-chat-empty small"><p>Loading conversations…</p></div>
                    ) : convs.length === 0 ? (
                        <div className="bp-chat-empty">
                            <span aria-hidden="true">💬</span>
                            <p>No conversations yet.<br />Message a shop from its page to start one.</p>
                        </div>
                    ) : (
                        convs.map(c => (
                            <button className="bp-conv" key={c.id} onClick={() => openConv(c)}>
                                <span className="bp-conv-avatar">
                                    {c.other_avatar
                                        ? <img alt="" src={c.other_avatar} />
                                        : (c.other_name || c.other_address || '?').replace('0x', '').charAt(0).toUpperCase()}
                                </span>
                                <span className="bp-conv-main">
                                    <strong>{c.other_name || short(c.other_address)}</strong>
                                    <small className="bp-dim">{c.last_message_text || c.last_message || 'Conversation'}</small>
                                </span>
                                {Number(c.my_unread_count) > 0 && <em className="bp-conv-unread">{c.my_unread_count}</em>}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );

    if (inline) return inner;
    return (
        <div className="bp-chat-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
            {inner}
        </div>
    );
}
